import React, { DependencyList, ReactElement, useCallback, useState } from 'react'
import { Typography } from '@equinor/eds-core-react'

import { useModal } from 'react-modal-hook'
import ConfirmDialog from '@/components/confirmDialog'
import { ConfirmDialogType } from '@/core/models/types'

export interface ConfirmDialogOptions<TData> {
    /** Dialog title. */
    title?: string

    /** Content of the dialog. */
    content?: string | ReactElement | ((data?: TData) => string | ReactElement)

    /** Called when dialog is confirmed. */
    onConfirm?: (data?: TData) => void

    /** Called when dialog is rejected. */
    onReject?: (data?: TData) => void

    /** Overrides confirmation button title. */
    rejectButtonTitle?: string

    /** Overrides confirmation button title. */
    confirmButtonTitle?: string

    /** Is the reject button disabled. */
    rejectButtonDisabled?: boolean

    /** Is the confirm button disabled. */
    confirmButtonDisabled?: boolean

    /** Is the busy indicator visible. */
    isBusy?: boolean

    /**
     * Should close dialog when reject or confirm button is clicked.
     * The dialog is closed by default.
     */
    autoClose?: boolean

    type?: ConfirmDialogType
}

/**
 * Opens a confirmation dialog.
 *
 * Custom data can be passed to the dialog as argument of the `showConfirm` function.
 *
 * @param options Confirm dialog options.
 * @param inputs An array of values referenced inside the dialog component.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useConfirmDialog = <TData extends unknown>(
    options: ConfirmDialogOptions<TData>,
    inputs?: DependencyList
) => {
    const [data, setData] = useState<TData>()

    const [showModal, hideModal] = useModal(() => {
        const content: string | ReactElement | undefined =
            typeof options.content === 'function' ? options.content(data) : options.content
        return (
            <ConfirmDialog
                title={options.title}
                onConfirmClick={onConfirmClick}
                onRejectClick={onRejectClick}
                rejectButtonTitle={options.rejectButtonTitle}
                confirmButtonTitle={options.confirmButtonTitle}
                rejectButtonDisabled={options.rejectButtonDisabled}
                confirmButtonDisabled={options.confirmButtonDisabled}
                isBusy={options.isBusy}
                type={options.type}
            >
                <>{typeof content === 'string' ? <Typography variant="body_short">{content}</Typography> : content}</>
            </ConfirmDialog>
        )
    }, [data, inputs])

    const onConfirmClick = () => {
        options.onConfirm?.(data)
        if (options.autoClose === true || options.autoClose === undefined) {
            hideModal()
        }
    }

    const onRejectClick = () => {
        options.onReject?.(data)
        if (options.autoClose === true || options.autoClose === undefined) {
            hideModal()
        }
    }

    const showConfirm = useCallback(
        (data?: TData) => {
            setData(data)
            showModal()
        },
        [showModal]
    )

    const hideConfirm = useCallback(() => {
        hideModal()
    }, [hideModal])

    return {
        showConfirm,
        hideConfirm,
    }
}
